import clsx from 'clsx';
import HttpCodes from 'http-status-codes';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ViewMode } from '@/builder/builder.constants';
import { runBuilder } from '@/builder/builder.utilities';
import { sqWorkbenchStore } from '@/core/core.stores';
import {
  getLoginLink,
  getPresentWorksheetLink,
  getViewWorksheetLink,
  getWorksheetLink,
  navigateToConnectionErrorPage,
  setFromBuilder,
} from '@/main/routing.utilities';
import { fetchConfiguration } from '@/services/systemConfiguration.actions';
import { switchLanguage } from '@/utilities/i18n.utilities';
import { logError } from '@/utilities/logger';
import { errorToast } from '@/utilities/toast.utilities';
import { formatApiError } from '@/utilities/utilities';
import { setStateParams } from '@/workbench/workbench.actions';
import { isAxiosError } from '@seeqdev/api';
import { Alert, LoadingIndicator } from '@seeqdev/qomponents';

export const Builder = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const searchParamsAsObject = Object.fromEntries(new URLSearchParams(searchParams)) as any;
  const trendItems = _.reject(searchParams.getAll('trendItems'), _.isEmpty);
  setStateParams({ ...params, ...searchParamsAsObject, trendItems });
  const stateParams = { ...params, ...searchParamsAsObject, trendItems };
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [buildError, setBuildError] = useState<any | null>(null);

  // Sanitize the input
  const builderParams = {
    ...stateParams,
    viewMode: _.isString(stateParams.viewMode) ? (_.toLower(stateParams.viewMode) as ViewMode) : ViewMode.Edit,
    startFresh: _.isString(stateParams.startFresh) ? _.toLower(stateParams.startFresh) === 'true' : true,
  };

  useEffect(() => {
    setFromBuilder(true);
    fetchConfiguration().then(
      () =>
        switchLanguage(sqWorkbenchStore.userLanguage).then(() =>
          runBuilder(builderParams)
            .then(({ workbookId, worksheetId }) => {
              if (builderParams.viewMode === ViewMode.View) {
                navigate(getViewWorksheetLink(workbookId, worksheetId));
              } else if (builderParams.viewMode === ViewMode.Present) {
                navigate(getPresentWorksheetLink(workbookId, worksheetId));
              } else {
                navigate(getWorksheetLink(workbookId, worksheetId));
              }
            })
            .catch((e) => {
              if (_.get(e, 'status') === HttpCodes.UNAUTHORIZED) {
                navigate(getLoginLink(location.pathname + location.search));
              } else {
                logError(formatApiError(e));
                setBuildError(e);
              }
            }),
        ),
      (error) => {
        // The auth interceptor sends unauthorized users to the login page
        if (!(isAxiosError(error) && error.response?.status === HttpCodes.UNAUTHORIZED)) {
          errorToast({ httpResponseOrError: error });
          navigateToConnectionErrorPage();
        }
      },
    );
  }, []);

  return (
    <div className="flexRowContainer fullViewport splashScreen text-white tw:items-center tw:justify-center">
      <LoadingIndicator size="xl" color="currentColor" extraClassNames="mb10" />
      <span className={clsx({ 'tw:hidden': buildError })}>{t('LOADING')}</span>
      {buildError && (
        <Alert variant="danger" dismissible={false}>
          <p>
            <span>{t('ERROR')}</span>
            {buildError.toString()}
          </p>
        </Alert>
      )}
    </div>
  );
};
